import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import axios from "../../api/axios";
import logoADUTEM from "../../assets/img/logoadutem.png";

// ───────────────────────────────────────────────
// Componente principal: VerCuotas
// ───────────────────────────────────────────────
const VerCuotas = () => {
  const navigate = useNavigate();

  const [data, setData] = useState({ meses: [] });
  const [usuario, setUsuario] = useState(null);     // ← Nuevo estado
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fechaDesde, setFechaDesde] = useState("");
const [fechaHasta, setFechaHasta] = useState("");

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [perfilRes, cuotasRes] = await Promise.all([
          axios.get("/perfil"),           // ← Extrae nombre del socio
          axios.get("/mi-total-mensual"),
        ]);

        // Guardar datos del perfil
        if (perfilRes.data?.ok) {
          setUsuario(perfilRes.data.perfil || perfilRes.data.usuario || null);
        }

        // Guardar datos de cuotas
        setData(cuotasRes.data || { meses: [] });

      } catch (err) {
        console.error("Error al cargar datos:", err);
        const mensaje =
          err.response?.data?.msg ||
          err.response?.data?.message ||
          err.message ||
          "Error al cargar el historial de cuotas";
        setError(mensaje);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // ───────────────────────────────────────────────
  // Exportar historial a Excel (una hoja por año)
  // ───────────────────────────────────────────────
  const exportarExcel = () => {
  if (!mesesFiltrados || mesesFiltrados.length === 0) return;

  // Agrupamos los meses filtrados por año
  const mesesPorAnio = mesesFiltrados.reduce((acc, mesData) => {
    const anio = mesData.anio ?? "Sin año";
    if (!acc[anio]) acc[anio] = [];
    acc[anio].push(mesData);
    return acc;
  }, {});

  const workbook = XLSX.utils.book_new();

    // Una hoja por año
    Object.keys(mesesPorAnio)
      .sort((a, b) => Number(a) - Number(b))
      .forEach((anio) => {
        const filas = [];

        mesesPorAnio[anio].forEach((mesData) => {
          const cuotas = mesData.detalles?.cuotas ?? [];
          const descuentos = mesData.detalles?.descuentos ?? [];
          const maxFilas = Math.max(cuotas.length, descuentos.length, 1);

          for (let i = 0; i < maxFilas; i++) {
            const cuota = cuotas[i];
            const descuento = descuentos[i];

            filas.push({
              Mes: i === 0 ? mesData.mes : "",
              "Cuota - Nombre": cuota?.nombre ?? "",
              "Cuota - Valor": cuota?.valor ?? "",
              "Descuento - Nombre": descuento?.nombre ?? "",
              "Descuento - Valor": descuento?.valor ?? "",
              "Descuento - Fecha": descuento?.fecha ?? "",
            });
          }

          // Fila de totales del mes
          filas.push({
            Mes: `Total ${mesData.mes}`,
            "Cuota - Nombre": "",
            "Cuota - Valor": mesData.total_cuotas ?? 0,
            "Descuento - Nombre": "",
            "Descuento - Valor": mesData.total_descuentos ?? 0,
            "Descuento - Fecha": "",
          });

          // Fila vacía como separador visual entre meses
          filas.push({});
        });

        const hoja = XLSX.utils.json_to_sheet(filas);

        // Anchos de columna razonables
        hoja["!cols"] = [
          { wch: 14 }, // Mes
          { wch: 28 }, // Cuota - Nombre
          { wch: 14 }, // Cuota - Valor
          { wch: 28 }, // Descuento - Nombre
          { wch: 16 }, // Descuento - Valor
          { wch: 14 }, // Descuento - Fecha
        ];

        // El nombre de la hoja no puede pasar 31 caracteres
        const nombreHoja = `Año ${anio}`.slice(0, 31);
        XLSX.utils.book_append_sheet(workbook, hoja, nombreHoja);
      });

    const nombreSocio = usuario?.nombres
      ? `${usuario.nombres} ${usuario.apellidos || ""}`.trim()
      : "Socio";
    const nombreArchivo = `Cuotas_${nombreSocio}.xlsx`.replace(/\s+/g, "_");
    XLSX.writeFile(workbook, nombreArchivo);
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={() => navigate(0)} />;

  // Extraemos el nombre exactamente como en DashboardSocio
  const socioNombre = usuario?.nombres || "Socio";
  const socioApellido = usuario?.apellidos || "";
  const nombreCompleto = `${socioNombre} ${socioApellido}`.trim();
  const primeraLetra = socioNombre.charAt(0).toUpperCase() || "S";
  const mesesFiltrados = data.meses.filter((mesData) => {
  // Convertir el nombre del mes a número
  const meses = {
    enero: 0,
    febrero: 1,
    marzo: 2,
    abril: 3,
    mayo: 4,
    junio: 5,
    julio: 6,
    agosto: 7,
    septiembre: 8,
    octubre: 9,
    noviembre: 10,
    diciembre: 11,
  };

  const fechaMes = new Date(
    Number(mesData.anio),
    meses[mesData.mes.toLowerCase()],
    1
  );

  if (fechaDesde) {
    const desde = new Date(fechaDesde);
    desde.setDate(1);

    if (fechaMes < desde) return false;
  }

  if (fechaHasta) {
    const hasta = new Date(fechaHasta);
    hasta.setDate(1);

    if (fechaMes > hasta) return false;
  }

  return true;
});

  return (
    <>
      {/* Navbar Mejorado - Ahora recibe nombreCompleto */}
      <Navbar socioNombre={nombreCompleto} primeraLetra={primeraLetra} />

      <main className="container py-5">
        <header className="d-flex justify-content-between align-items-center mb-5 flex-wrap gap-3">
          <div>
            <h1 className="fw-bold" style={{ color: "#00993E" }}>
              <i className="bi bi-list-ul me-3 text-success"></i>
              Mis Cuotas y Descuentos
            </h1>
            <p className="text-muted mb-0">Historial mensual detallado</p>

            <div className="row g-3 mb-4 align-items-end">

  <div className="col-md-4">
    <label className="form-label fw-semibold">
      Desde
    </label>
    <input
      type="month"
      className="form-control"
      value={fechaDesde}
      onChange={(e) => setFechaDesde(e.target.value)}
    />
  </div>

  <div className="col-md-4">
    <label className="form-label fw-semibold">
      Hasta
    </label>
    <input
      type="month"
      className="form-control"
      value={fechaHasta}
      onChange={(e) => setFechaHasta(e.target.value)}
    />
  </div>

  <div className="col-md-2">
<button
  className="btn d-flex align-items-center justify-content-center gap-2 fw-semibold shadow-sm"
  style={{
    backgroundColor: "#9b9e9a",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    height: "40px",
    width: "100px",
    padding: "0 20px",
  }}
  onClick={() => {
    setFechaDesde("");
    setFechaHasta("");
  }}
>
  <i className="bi bi-arrow-counterclockwise fs-5"></i>
  <span>Limpiar</span>
</button>
  </div>

</div>
          </div>

          {data.meses?.length > 0 && (
            <button
              className="btn d-flex align-items-center gap-2 px-4 py-2 fw-medium"
              style={{ backgroundColor: "#00993E", color: "#fff", border: "none" }}
              onClick={exportarExcel}
            >
              <i className="bi bi-file-earmark-excel-fill fs-5"></i>
              Exportar a Excel
            </button>
          )}
        </header>

        {mesesFiltrados.length === 0 ? (
          <div className="alert alert-info text-center py-5 shadow-sm">
            <i className="bi bi-info-circle-fill me-2 fs-4"></i>
            Aún no tienes registros de cuotas o descuentos.
          </div>
        ) : (
          <div className="accordion accordion-flush" id="cuotasAccordion">
            {mesesFiltrados.map((mesData, index) => (
              <div
                key={index}
                className="accordion-item border-0 shadow-sm mb-3 rounded overflow-hidden"
              >
                <h2 className="accordion-header">
                  <button
                    className="accordion-button collapsed fw-bold py-3 bg-light"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#mes-${index}`}
                  >
                    <div className="d-flex align-items-center flex-wrap gap-3 w-100">
                      <span className="fs-5">
                        {mesData.mes} {mesData.anio}
                      </span>
                      <span
  className="badge px-3 py-2"
  style={{
    backgroundColor: "#00993E",
    color: "#fff"
  }}
>
  Total: ${mesData.total ?? 0}
</span>

<span
  className="badge px-3 py-2"
  style={{
    backgroundColor: "#6c757d",
    color: "#fff"
  }}
>
  {mesData.detalles?.cuotas?.length ?? 0} cuotas
</span>

{mesData.total_descuentos > 0 && (
  <span
    className="badge px-3 py-2"
    style={{
      backgroundColor: "#FFD123",
      color: "#212529"
    }}
  >
    ${mesData.total_descuentos} descuentos
  </span>
)}
                    </div>
                  </button>
                </h2>

                <div id={`mes-${index}`} className="accordion-collapse collapse">
                  <div className="accordion-body bg-white">
                    <div className="row g-4">
                      {/* Cuotas */}
                      <div className="col-lg-6">
                        <SectionCard
                          title="Cuotas"
                          count={`$${mesData.total_cuotas ?? 0}`}
                          color="cuotas"
                        >
                          {mesData.detalles?.cuotas?.length === 0 ? (
                            <p className="text-muted text-center py-4">
                              No hay cuotas registradas este mes
                            </p>
                          ) : (
                            <ul className="list-group list-group-flush">
                              {mesData.detalles.cuotas.map((c, i) => (
                                <li
                                  key={i}
                                  className="list-group-item d-flex justify-content-between align-items-center"
                                >
                                  <span>{c.nombre}</span>
                                  <strong className="text-success">${c.valor}</strong>
                                </li>
                              ))}
                            </ul>
                          )}
                        </SectionCard>
                      </div>

                      {/* Descuentos */}
                      <div className="col-lg-6">
                        <SectionCard
                          title="Convenios, Seguros y más"
                          count={`$${mesData.total_descuentos ?? 0}`}
                          color="descuentos"
                        >
                          {mesData.detalles?.descuentos?.length === 0 ? (
                            <p className="text-muted text-center py-4">
                              No hay descuentos aplicados este mes
                            </p>
                          ) : (
                            <ul className="list-group list-group-flush">
                              {mesData.detalles.descuentos.map((d, i) => (
                                <li
                                  key={i}
                                  className="list-group-item d-flex justify-content-between align-items-center"
                                >
                                  <div>
                                    <span>{d.nombre}</span>
                                    <small className="text-muted d-block">{d.fecha}</small>
                                  </div>
                                  <strong className="text-success">+${d.valor}</strong>
                                </li>
                              ))}
                            </ul>
                          )}
                        </SectionCard>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </>
  );
};

// ───────────────────────────────────────────────
// NAVBAR MEJORADO (sin cambios)
// ───────────────────────────────────────────────
const Navbar = ({ socioNombre, primeraLetra }) => {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login", { replace: true });
  };

  return (
    <nav 
      className="navbar navbar-expand-lg navbar-dark shadow-sm"
      style={{
  background: "#FFD123",
  padding: "18px 0",
  position: "sticky",
  top: 0,
  zIndex: 1000,
}}
    >
      <div className="container-fluid px-4">
        
        <Link className="navbar-brand d-flex align-items-center" to="/DashboardSocio">
          <img 
            src={logoADUTEM} 
            alt="ADUTEM" 
            style={{ height: "68px" }} 
          />
        </Link>

        <button
          className="navbar-toggler border-0"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav mx-auto gap-1">
            {[
              { to: "/DashboardSocio", label: "Inicio", end: true },
              { to: "/vercuotas", label: "Mis Cuotas" },
              { to: "/reservasSocios", label: "Reservas" },
              { to: "/MisReservas", label: "Mis Reservas" },
            ].map(({ to, label, end = false }) => (
              <li className="nav-item" key={to}>
                <NavLink
                  to={to}
                  end={end}
                  className={({ isActive }) => 
                    `nav-link px-4 py-2 rounded-pill fw-medium transition-all ${isActive 
                      ? "active bg-white text-dark shadow-sm" 
                      : "text-white hover-bg-white hover-text-dark"}`
                  }
                  style={{ fontSize: "1.02rem" }}
                >
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="dropdown">
            <button
              className="btn btn-link text-decoration-none p-0 border-0 d-flex align-items-center gap-2"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <div
                className="rounded-circle d-flex align-items-center justify-content-center shadow"
                style={{
                  width: "42px",
                  height: "42px",
                  backgroundColor: "#f1c40f",
                  color: "#2c3e50",
                  fontWeight: "700",
                  fontSize: "1.25rem",
                  border: "2px solid rgba(255,255,255,0.6)"
                }}
              >
                {primeraLetra}
              </div>
              <div className="text-white text-start d-none d-md-block">
                <div className="fw-medium" style={{ fontSize: "1.02rem" }}>{socioNombre}</div>
                <small className="opacity-75" style={{ fontSize: "0.82rem" }}>Socio</small>
              </div>
              <i className="bi bi-chevron-down text-white ms-1" style={{ fontSize: "1rem" }}></i>
            </button>

            <ul className="dropdown-menu dropdown-menu-start shadow-lg border-0 py-2">
              <li>
                <Link className="dropdown-item d-flex align-items-center py-2" to="/perfil">
                  <i className="bi bi-person-circle me-3 fs-5"></i>
                  Mi Perfil
                </Link>
              </li>
              <li><hr className="dropdown-divider my-1" /></li>
              <li>
                <button 
                  className="dropdown-item d-flex align-items-center py-2 text-danger" 
                  onClick={handleLogout}
                >
                  <i className="bi bi-box-arrow-right me-3 fs-5"></i>
                  Cerrar sesión
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Loading y Error States (sin cambios)
const LoadingState = () => (
  <div className="min-vh-100 d-flex align-items-center justify-content-center">
    <div className="text-center">
      <div className="spinner-border text-success" style={{ width: "3.5rem", height: "3.5rem" }} role="status">
        <span className="visually-hidden">Cargando...</span>
      </div>
      <p className="mt-4 text-muted fs-5">Cargando historial de cuotas...</p>
    </div>
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="min-vh-100 d-flex align-items-center justify-content-center p-4">
    <div className="alert alert-danger text-center shadow-lg p-5" style={{ maxWidth: "550px" }}>
      <i className="bi bi-exclamation-triangle-fill me-3 fs-3"></i>
      <h4 className="alert-heading mb-3">Algo salió mal</h4>
      <p className="mb-4">{message || "No pudimos cargar el historial"}</p>
      <button className="btn btn-outline-danger px-5" onClick={onRetry}>
        Reintentar
      </button>
    </div>
  </div>
);

const SectionCard = ({ title, count, color, children }) => {
  const colores = {
    cuotas: "#00993E",
    descuentos: "#FFD123",
  };

  return (
    <div
  className="card h-100 shadow-sm"
  style={{
    borderColor: colores[color],
    borderWidth: "1px",
    borderStyle: "solid",
    overflow: "hidden"
  }}
>
      <div
        className="card-header fw-bold d-flex justify-content-between align-items-center"
        style={{
          backgroundColor: colores[color],
          color: color === "descuentos" ? "#212529" : "#fff",
        }}
      >
        <span>{title}</span>

        <span
          className="badge"
          style={{
            backgroundColor: "#fff",
            color: "#212529",
          }}
        >
          {count}
        </span>
      </div>

      <div className="card-body p-0">{children}</div>
    </div>
  );
};

export default VerCuotas;